// @ts-strict-ignore
import React, { memo, useRef } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { Button } from '@actual-app/components/button';
import { SvgRightArrow2 } from '@actual-app/components/icons/v0';
import { SpaceBetween } from '@actual-app/components/space-between';
import { styles } from '@actual-app/components/styles';
import { Text } from '@actual-app/components/text';
import { theme } from '@actual-app/components/theme';
import { View } from '@actual-app/components/view';
import type { RuleEntity } from '@actual-app/core/types/models';

import { Cell, Field, Row, SelectCell } from '#components/table';
import { useContextMenu } from '#hooks/useContextMenu';
import { useSelectedDispatch } from '#hooks/useSelected';
import { useIsNocturneTheme } from '#style';
import {
  friendlyOp,
  groupActionsBySplitIndex,
  translateRuleStage,
} from '#util/rule';

import { ActionExpression } from './ActionExpression';
import { ConditionExpression } from './ConditionExpression';

type RuleRowProps = {
  rule: RuleEntity;
  nocturneLayout?: boolean;
  order: number;
  hovered?: boolean;
  selected?: boolean;
  onHover?: (id: string | null) => void;
  onEditRule?: (rule: RuleEntity) => void;
  onDeleteRule?: (rule: RuleEntity) => void;
};

export const RuleRow = memo(
  ({
    rule,
    nocturneLayout,
    order,
    hovered,
    selected,
    onHover,
    onEditRule,
    onDeleteRule,
  }: RuleRowProps) => {
    const isNocturneTheme = useIsNocturneTheme();
    const isNocturneLayout = nocturneLayout ?? isNocturneTheme;
    const dispatchSelected = useSelectedDispatch();
    const borderColor = selected ? theme.tableBorderSelected : 'none';
    const backgroundFocus = hovered;

    const actionSplits = groupActionsBySplitIndex(rule.actions);
    const hasSplits = actionSplits.length > 1;

    const hasSchedule = rule.actions.some(({ op }) => op === 'link-schedule');

    const { t } = useTranslation();

    const triggerRef = useRef(null);

    useContextMenu({
      triggerRef,
      items: [
        onEditRule && {
          name: 'edit',
          text: t('Edit'),
          onClick: () => onEditRule(rule),
        },
        onDeleteRule &&
          !hasSchedule && {
            name: 'delete',
            text: t('Delete'),
            onClick: () => onDeleteRule(rule),
          },
      ],
    });

    return (
      <Row
        ref={triggerRef}
        data-nocturne-rule-row={isNocturneLayout || undefined}
        tabIndex={isNocturneLayout ? 0 : undefined}
        role={isNocturneLayout ? 'group' : undefined}
        aria-label={
          isNocturneLayout ? t('Edit rule {{order}}', { order }) : undefined
        }
        aria-keyshortcuts={
          isNocturneLayout ? 'Enter ArrowUp ArrowDown' : undefined
        }
        height="auto"
        style={{
          fontSize: 13,
          zIndex: selected ? 101 : 'auto',
          borderColor,
          backgroundColor: isNocturneLayout
            ? theme.cardBackground
            : selected
              ? theme.tableRowBackgroundHighlight
              : backgroundFocus
                ? theme.tableRowBackgroundHover
                : theme.tableBackground,
          ...(isNocturneLayout && {
            cursor: 'pointer',
            marginBottom: 8.4,
            borderRadius: 8,
            overflow: 'hidden',
            boxShadow:
              hovered || selected
                ? `inset 2px 0 0 ${theme.tableBorderSelected}`
                : 'var(--shadow-card)',
          }),
        }}
        collapsed
        onClick={(event: React.MouseEvent<HTMLDivElement>) => {
          if (
            isNocturneLayout &&
            event.target instanceof HTMLElement &&
            !event.target.closest(
              'button, input, select, textarea, a[href], [role="button"], [role="checkbox"]',
            )
          ) {
            onEditRule?.(rule);
          }
        }}
        onKeyDown={(event: React.KeyboardEvent<HTMLDivElement>) => {
          if (!isNocturneLayout || event.target !== event.currentTarget) {
            return;
          }
          if (event.key === 'Enter') {
            if (event.repeat) {
              return;
            }
            event.preventDefault();
            onEditRule?.(rule);
            return;
          }
          if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') {
            return;
          }

          event.preventDefault();
          const rulesContainer = event.currentTarget.closest(
            '[data-nocturne-rules]',
          );
          const rows = Array.from(
            rulesContainer?.querySelectorAll<HTMLElement>(
              '[data-nocturne-rule-row]',
            ) ?? [],
          );
          const currentIndex = rows.indexOf(event.currentTarget);
          const nextIndex =
            event.key === 'ArrowDown' ? currentIndex + 1 : currentIndex - 1;
          rows[nextIndex]?.focus();
        }}
        onMouseEnter={() => onHover && onHover(rule.id)}
        onMouseLeave={() => onHover && onHover(null)}
      >
        <SelectCell
          exposed={hovered || selected}
          focused
          onSelect={e => {
            dispatchSelected({
              type: 'select',
              id: rule.id,
              isRangeSelect: e.shiftKey,
            });
          }}
          selected={selected}
        />

        {isNocturneLayout && (
          <Cell
            name="order"
            width={28}
            plain
            style={{
              alignItems: 'center',
              justifyContent: 'center',
              color: theme.pageTextSubdued,
              fontSize: 11,
              fontVariantNumeric: 'tabular-nums',
            }}
          >
            {order}
          </Cell>
        )}

        {!isNocturneLayout && (
          <Cell
            name="stage"
            width={50}
            plain
            style={{ color: theme.tableText }}
          >
            {rule.stage && (
              <View
                style={{
                  alignSelf: 'flex-start',
                  margin: 5,
                  backgroundColor: theme.pillBackgroundSelected,
                  color: theme.pillTextSelected,
                  borderRadius: 4,
                  padding: '3px 5px',
                }}
              >
                {translateRuleStage(rule.stage)}
              </View>
            )}
          </Cell>
        )}

        <Field
          width="flex"
          style={{ padding: isNocturneLayout ? '11.2px 0' : '15px 0' }}
          truncate={false}
        >
          <SpaceBetween style={{ alignItems: 'center' }}>
            <View
              style={{
                flex: 1,
                alignItems: 'flex-start',
                ...(isNocturneLayout && {
                  flexDirection: 'row',
                  gap: 5.6,
                  alignItems: 'center',
                }),
              }}
              data-testid="conditions"
            >
              {isNocturneLayout && rule.stage && (
                <Text
                  style={{
                    padding: '2px 5.6px',
                    border: `1px solid ${theme.tableBorder}`,
                    borderRadius: 4,
                    color: theme.pageTextSubdued,
                    fontSize: 10,
                    letterSpacing: '0.08em',
                    textTransform: 'uppercase',
                  }}
                >
                  {translateRuleStage(rule.stage)}
                </Text>
              )}
              {isNocturneLayout && (
                <Text
                  style={{
                    color: theme.pageTextSubdued,
                    fontSize: 10,
                    fontWeight: 500,
                    letterSpacing: '0.14em',
                  }}
                >
                  <Trans>IF</Trans>
                </Text>
              )}
              {rule.conditions.map((cond, i) => (
                <ConditionExpression
                  key={i}
                  field={cond.field}
                  op={cond.op}
                  inline
                  value={cond.value}
                  options={cond.options}
                  prefix={i > 0 ? friendlyOp(rule.conditionsOp) : null}
                  style={i !== 0 && { marginTop: 3 }}
                />
              ))}
            </View>

            <Text>
              {isNocturneLayout ? (
                <Text
                  style={{
                    color: theme.pageTextSubdued,
                    fontSize: 10,
                    fontWeight: 500,
                    letterSpacing: '0.14em',
                  }}
                >
                  <Trans>THEN</Trans>
                </Text>
              ) : (
                <SvgRightArrow2
                  style={{ width: 12, height: 12, color: theme.tableText }}
                />
              )}
            </Text>

            <View
              style={{
                flex: 1,
                alignItems: 'flex-start',
                ...(isNocturneLayout && {
                  flexDirection: 'row',
                  gap: 5.6,
                  alignItems: 'center',
                }),
              }}
              data-testid="actions"
            >
              {hasSplits
                ? actionSplits.map((split, i) => (
                    <View
                      key={split.id}
                      style={{
                        width: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'flex-start',
                        marginTop: i > 0 ? 3 : 0,
                        padding: '5px',
                        borderColor: theme.tableBorder,
                        borderWidth: '1px',
                        borderRadius: '5px',
                      }}
                    >
                      <Text
                        style={{
                          ...styles.smallText,
                          color: theme.pageTextLight,
                          marginBottom: 6,
                        }}
                      >
                        {i ? t('Split {{num}}', { num: i }) : t('Apply to all')}
                      </Text>
                      {split.actions.map((action, j) => (
                        <ActionExpression
                          key={j}
                          {...action}
                          style={j !== 0 && { marginTop: 3 }}
                        />
                      ))}
                    </View>
                  ))
                : rule.actions.map((action, i) => (
                    <ActionExpression
                      key={i}
                      {...action}
                      style={i !== 0 && { marginTop: 3 }}
                    />
                  ))}
            </View>
          </SpaceBetween>
        </Field>

        {!isNocturneLayout && (
          <Cell name="edit" plain style={{ padding: '0 15px', paddingLeft: 5 }}>
            <Button onPress={() => onEditRule(rule)}>
              <Trans>Edit</Trans>
            </Button>
          </Cell>
        )}
      </Row>
    );
  },
);

RuleRow.displayName = 'RuleRow';
